import * as _ from 'lodash'
import React, { FC, useMemo } from 'react'
import { Select, SelectProps } from '../../..'
import { ReactSelectOption } from '../../select/Select'
import { UserSelectQuery, useUserSelectLazyQuery } from './UserSelect.codegen'
import { useDebounce } from 'use-debounce'

export type UserInfo = UserSelectQuery['users'][0]

export type UserSelectProps = SelectProps & {
  value: UserInfo | null
  onChange: (user: UserInfo) => void
}

/**
 * select user from anywhere
 * - takes { id, display_name } as value
 * - onChange gets it too
 */
export const UserSelect: FC<UserSelectProps> = ({
  value,
  onChange,
  ...rest
}) => {
  const [searchUsers, { data, loading }] = useUserSelectLazyQuery()
  const users = data?.users ?? (value && [value])

  // current version restricts value to string but it works fine with number too
  // (2.0 will drop this restriction so we can remove that any)
  const opts: ReactSelectOption[] | undefined = useMemo(
    () => users?.map((u) => ({ label: u.display_name, value: u.id })),
    [data]
  )

  // there's a bug in react-select (fixed in 2.0 which is beta now)
  // https://github.com/JedWatson/react-select/issues/3335
  const loadOnce = useMemo(() => _.once(searchUsers), [])

  const [query, setQuery] = React.useState('')
  const [debouncedQuery] = useDebounce(query, 250)

  React.useEffect(() => {
    if (debouncedQuery) {
      searchUsers({ variables: { search: debouncedQuery } })
    }
  }, [debouncedQuery])

  return (
    <Select
      options={opts}
      value={opts?.find((o) => o.value === value?.id)}
      onChange={(o: any) => onChange(users!.find((u) => u.id === o.value)!)}
      isLoading={loading}
      onInputChange={(search, actionMeta) => {
        if (
          actionMeta.action === 'input-change' ||
          actionMeta.action === 'set-value'
        ) {
          setQuery(search)
          return search
        }
        return query
      }}
      inputValue={query}
      onMenuOpen={loadOnce}
      {...rest}
    />
  )
}
